import { useCallback, useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { BookMeta, ProgressEntry, LibrarySyncResponse, ProgressSyncResponse } from '@shared/types';
import { useAuth } from '../auth';
import { api } from '../api';
import { connectHub, disconnectHub, onPresence, downloadBook, type HubDevice, type HubBook } from '../hub';
import { saveBlobToDisk } from '../db';
import { isFolderSupported, getFolder, pickFolder, hasPermission, ensurePermission, fileNameFor } from '../fsfolder';
import { availableBookIds, storeDownload, resolveBookBlob } from '../library';
import { BookCard } from '../components/BookCard';

interface Shelf {
  book: BookMeta;
  progress?: ProgressEntry;
}
type DlState =
  | { status: 'downloading'; pct: number }
  | { status: 'error'; error: string };
interface FolderState {
  name: string;
  granted: boolean;
}

export function Home() {
  const { user, loading } = useAuth();
  if (loading) return <section className="hero"><p className="muted">Loading…</p></section>;
  if (!user) return <Landing />;
  return <Library />;
}

const HIGHLIGHTS: { icon: string; title: string; body: string }[] = [
  { icon: '🔄', title: 'Synced everywhere', body: 'Your place and library follow you across phone, web and Windows.' },
  { icon: '📖', title: 'EPUB & PDF reader', body: 'Read in your browser with themes and a one- or two-page layout.' },
  { icon: '🗣️', title: 'Natural voices', body: 'Lifelike read-aloud that runs privately on your device, even offline.' },
  { icon: '📁', title: 'Your files, yours', body: 'Reads from a folder on your PC — no lock-in, no uploads.' },
];

const STEPS: { n: string; title: string; body: string }[] = [
  { n: '1', title: 'Add your books', body: 'Open EPUBs and PDFs in the app, or point the web at your library folder.' },
  { n: '2', title: 'Sign in anywhere', body: 'Your library and reading position sync to every device automatically.' },
  { n: '3', title: 'Read or listen', body: 'Pick up where you left off — on screen or read aloud in a natural voice.' },
];

function Landing() {
  return (
    <div className="landing">
      <section className="lp-hero">
        <img src="/logo-wordmark.png" alt="ScrollWind" className="lp-logo" />
        <h1>Your library, on every screen.</h1>
        <p className="lede">
          Read your EPUBs and PDFs on phone, web, and Windows — with lifelike read-aloud voices. Sign
          in and your progress and library follow you between devices.
        </p>
        <div className="cta-row">
          <Link className="btn" to="/login">Get started</Link>
          <Link className="navlink" to="/features">See features →</Link>
        </div>
      </section>

      <section className="lp-strip">
        {HIGHLIGHTS.map((h) => (
          <div key={h.title} className="card lp-highlight">
            <span className="feat-icon" aria-hidden>{h.icon}</span>
            <h3>{h.title}</h3>
            <p className="muted">{h.body}</p>
          </div>
        ))}
      </section>

      <section className="lp-steps">
        <h2>How it works</h2>
        <div className="steps">
          {STEPS.map((s) => (
            <div key={s.n} className="step">
              <span className="step-n">{s.n}</span>
              <h3>{s.title}</h3>
              <p className="muted">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-cta card">
        <h2>Bring your books with you.</h2>
        <p className="muted">Free to use with your own files. No uploads, no lock-in.</p>
        <Link className="btn" to="/login">Create your account</Link>
      </section>
    </div>
  );
}

function Section({ title, count, defaultOpen = true, children }: { title: string; count: number; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="section">
      <button type="button" className="sectionhead" onClick={() => setOpen((o) => !o)}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        <span className="sectiontitle">{title}</span>
        <span className="muted">· {count}</span>
      </button>
      {open && children}
    </div>
  );
}

function Library() {
  const [shelf, setShelf] = useState<Shelf[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<HubDevice[]>([]);
  const [dl, setDl] = useState<Record<string, DlState>>({});
  const [available, setAvailable] = useState<Set<string>>(new Set());
  const [folder, setFolder] = useState<FolderState | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [cols, setCols] = useState<number>(() => {
    const v = Number(localStorage.getItem('sw-lib-cols'));
    return v >= 2 && v <= 4 ? v : 2;
  });
  const supported = isFolderSupported();

  const chooseCols = (n: number) => {
    setCols(n);
    localStorage.setItem('sw-lib-cols', String(n));
  };

  const refreshAvailability = useCallback(async (books: { id: string; title: string }[]) => {
    try {
      setAvailable(await availableBookIds(books));
    } catch {
      // ignore
    }
  }, []);

  const loadFolderState = useCallback(async () => {
    const h = await getFolder().catch(() => undefined);
    if (!h) return setFolder(null);
    setFolder({ name: h.name, granted: await hasPermission(h, false) });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadFolderState();
      const [lib, prog] = await Promise.all([
        api<LibrarySyncResponse>('/api/library'),
        api<ProgressSyncResponse>('/api/progress'),
      ]);
      if (!alive) return;
      const byId = new Map(prog.entries.map((e) => [e.bookId, e]));
      setShelf(
        lib.books
          .map((book) => ({ book, progress: byId.get(book.id) }))
          .sort((a, b) => (b.progress?.at ?? 0) - (a.progress?.at ?? 0)),
      );
      await refreshAvailability(lib.books.map((b) => ({ id: b.id, title: b.title })));
    })().catch((e) => alive && setError(String(e?.message ?? e)));
    return () => {
      alive = false;
    };
  }, [loadFolderState, refreshAvailability]);

  useEffect(() => {
    connectHub();
    const off = onPresence(setDevices);
    return () => {
      off();
      disconnectHub();
    };
  }, []);

  const shelfBooks = () => (shelf ?? []).map((s) => ({ id: s.book.id, title: s.book.title }));

  const chooseFolder = async () => {
    try {
      const h = await pickFolder();
      if (h) {
        setFolder({ name: h.name, granted: true });
        await refreshAvailability(shelfBooks());
      }
    } catch {
      /* cancelled */
    }
  };
  const reconnectFolder = async () => {
    const h = await getFolder();
    if (h && (await ensurePermission(h, false))) {
      setFolder({ name: h.name, granted: true });
      await refreshAvailability(shelfBooks());
    }
  };
  const rescanFolder = async () => {
    setFolderBusy(true);
    await refreshAvailability(shelfBooks());
    setFolderBusy(false);
  };

  const setBookDl = (id: string, state: DlState | undefined) =>
    setDl((prev) => {
      const next = { ...prev };
      if (state) next[id] = state;
      else delete next[id];
      return next;
    });

  const handleDownload = async (deviceId: string, book: HubBook) => {
    setBookDl(book.id, { status: 'downloading', pct: 0 });
    try {
      const blob = await downloadBook(deviceId, book, (f) => setBookDl(book.id, { status: 'downloading', pct: f }));
      await storeDownload(book.id, book.title, book.type, blob);
      setAvailable((prev) => new Set(prev).add(book.id));
      setBookDl(book.id, undefined);
    } catch (e) {
      setBookDl(book.id, { status: 'error', error: String((e as Error)?.message ?? e) });
    }
  };

  const handleSaveToDisk = async (id: string, title: string, type: 'PDF' | 'EPUB') => {
    const blob = await resolveBookBlob(id, title);
    if (blob) saveBlobToDisk(blob, fileNameFor(title, type));
  };

  const usingFolder = !!folder?.granted;

  // Map of bookId -> a device currently offering it for download.
  const offeredBy = new Map<string, { deviceId: string; book: HubBook }>();
  for (const d of devices) for (const b of d.books) if (!offeredBy.has(b.id)) offeredBy.set(b.id, { deviceId: d.deviceId, book: b });

  const downloaded = (shelf ?? []).filter((s) => available.has(s.book.id));
  const notDownloaded = (shelf ?? []).filter((s) => !available.has(s.book.id));
  const continueReading = downloaded.filter((s) => s.progress);

  return (
    <section className="library" style={{ '--cols': cols } as CSSProperties}>
      <div className="libhead">
        <h1>Your library</h1>
        {/* Grid density — shown on mobile, where covers would otherwise be large. */}
        <div className="colctrl" role="group" aria-label="Columns">
          <span className="muted small">Columns</span>
          {[2, 3, 4].map((n) => (
            <button
              key={n}
              type="button"
              className={cols === n ? 'ctrlbtn active' : 'ctrlbtn'}
              onClick={() => chooseCols(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Library folder */}
      {supported ? (
        <div className="card folderbar">
          {folder ? (
            folder.granted ? (
              <>
                <span>📁 Library folder: <b>{folder.name}</b></span>
                <span className="rowactions">
                  <button className="btn btn-small" onClick={rescanFolder} disabled={folderBusy}>
                    {folderBusy ? 'Scanning…' : 'Rescan'}
                  </button>
                  <button className="btn btn-small btn-ghost" onClick={chooseFolder}>Change</button>
                </span>
              </>
            ) : (
              <>
                <span>📁 <b>{folder.name}</b> <span className="muted">— reconnect to use it this session</span></span>
                <button className="btn btn-small" onClick={reconnectFolder}>Reconnect</button>
              </>
            )
          ) : (
            <>
              <span className="muted">Choose a folder on this PC to read your books from and save downloads into.</span>
              <button className="btn btn-small" onClick={chooseFolder}>Choose folder</button>
            </>
          )}
        </div>
      ) : null}

      {error && <p className="err">Couldn't load your library: {error}</p>}
      {!shelf && !error && <p className="muted">Loading your library…</p>}

      {shelf && (
        <>
          {/* Continue reading — downloaded books with progress */}
          {continueReading.length > 0 && (
            <Section title="Continue reading" count={continueReading.length}>
              <div className="bookgrid">
                {continueReading.map(({ book, progress }) => (
                  <BookCard key={book.id} book={book} available progressPct={progress!.pct}>
                    <Link className="btn btn-small" to={`/read/${encodeURIComponent(book.id)}`}>Read</Link>
                  </BookCard>
                ))}
              </div>
            </Section>
          )}

          {/* Downloaded / in your folder */}
          <Section title={usingFolder ? 'In your library folder' : 'Downloaded here'} count={downloaded.length}>
            {downloaded.length === 0 ? (
              <p className="muted">Nothing downloaded yet. Grab books from “Not downloaded” below while your phone is online.</p>
            ) : (
              <div className="bookgrid">
                {downloaded.map(({ book, progress }) => (
                  <BookCard key={book.id} book={book} available progressPct={progress?.pct}>
                    <Link className="btn btn-small" to={`/read/${encodeURIComponent(book.id)}`}>Read</Link>
                    {!usingFolder && (
                      <button className="btn btn-small btn-ghost" onClick={() => handleSaveToDisk(book.id, book.title, book.type)}>
                        Save
                      </button>
                    )}
                  </BookCard>
                ))}
              </div>
            )}
          </Section>

          {/* Not downloaded — pull from phone when it's online */}
          <Section title="Not downloaded" count={notDownloaded.length} defaultOpen={false}>
            {notDownloaded.length === 0 ? (
              <p className="muted">Everything in your library is available here. 🎉</p>
            ) : (
              <div className="bookgrid">
                {notDownloaded.map(({ book }) => {
                  const offer = offeredBy.get(book.id);
                  const st = dl[book.id];
                  return (
                    <BookCard key={book.id} book={book} available={false}>
                      {st?.status === 'downloading' ? (
                        <span className="muted">{Math.round(st.pct * 100)}%</span>
                      ) : offer ? (
                        <button className="btn btn-small" onClick={() => handleDownload(offer.deviceId, offer.book)}>
                          {st?.status === 'error' ? 'Retry' : 'Download'}
                        </button>
                      ) : (
                        <span className="muted small">On your phone</span>
                      )}
                    </BookCard>
                  );
                })}
              </div>
            )}
          </Section>

          {shelf.length === 0 && (
            <div className="card">
              <p className="muted">
                Your library is empty. Add books on your phone (they sync here), then download them to read.
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
